import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { SignedMedia } from "@/lib/signedFile";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, CheckCircle2, Play, FileText, Link2,
  Video, ClipboardCheck, Trophy, Circle, X,
} from "lucide-react";

export default function CourseViewer() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMaterial, setActiveMaterial] = useState(null);
  const [activeQuiz, setActiveQuiz] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get(`/courses/${id}`);
      setData(d);
      // auto-open first material if none selected
      if (!activeMaterial && !activeQuiz) {
        for (const s of d.sections || []) {
          if ((s.materials || []).length) {
            setActiveMaterial(s.materials[0]);
            break;
          }
        }
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const completeMaterial = async (mid) => {
    try {
      await api.post(`/materials/${mid}/complete`);
      toast.success("تم احتساب المادة");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !data) {
    return <DashboardLayout><div className="grid place-items-center py-32"><Loader2 className="h-10 w-10 animate-spin text-teal-700" /></div></DashboardLayout>;
  }

  const { course, sections, enrollment } = data;
  const completed = new Set(enrollment?.completed_material_ids || []);
  const quizScores = enrollment?.quiz_scores || {};

  return (
    <DashboardLayout>
      <div data-testid="course-viewer">
        <Link to="/dashboard/my-courses" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-teal-700 mb-4">
          <ChevronLeft className="h-4 w-4" /> العودة لبرنامج الإعداد والتهيئة
        </Link>

        <div className="mb-6 p-5 rounded-3xl bg-gradient-to-br from-teal-700 to-teal-900 text-white relative overflow-hidden">
          <div className="absolute -end-16 -top-16 h-64 w-64 rounded-full bg-amber-400/10 blur-3xl" />
          <div className="relative">
            <div className="text-xs font-bold uppercase tracking-widest text-amber-300 mb-2">وحدة التعلّم الإلكتروني</div>
            <h1 className="text-3xl lg:text-4xl font-heading font-black leading-tight">{course.title}</h1>
            <div className="mt-4 max-w-md">
              <div className="flex items-center justify-between text-sm mb-2">
                <span>تقدّم المقرر</span>
                <span className="font-bold arabic-numerals">{Math.round(enrollment?.progress_percent || 0)}%</span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${enrollment?.progress_percent || 0}%` }} />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Content viewer (larger, LEFT in RTL) */}
          <div className="lg:col-span-2 order-2 lg:order-1">
            {activeMaterial && <MaterialViewer material={activeMaterial} completed={completed.has(activeMaterial.id)} onComplete={() => completeMaterial(activeMaterial.id)} />}
            {activeQuiz && <QuizPlayer quiz={activeQuiz} bestScore={quizScores[activeQuiz.id]} onDone={load} onClose={() => setActiveQuiz(null)} />}
            {!activeMaterial && !activeQuiz && (
              <div className="p-16 rounded-2xl bg-white border border-slate-200 text-center">
                <Play className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                <div className="text-slate-600">اختر مادة من القائمة للبدء بالتعلّم</div>
              </div>
            )}
          </div>

          {/* Sidebar list (RIGHT in RTL) */}
          <div className="lg:col-span-1 order-1 lg:order-2">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden sticky top-24">
              <div className="p-4 border-b border-slate-100 bg-slate-50">
                <div className="font-heading font-bold text-slate-900">محتوى المقرر</div>
                <div className="text-xs text-slate-500 mt-1 arabic-numerals">{sections.length} قسم</div>
              </div>
              <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
                {sections.map((s, si) => (
                  <div key={s.id} className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-7 w-7 rounded-lg bg-teal-700 text-white grid place-items-center text-xs font-bold arabic-numerals">
                        {String(si + 1).padStart(2, "0")}
                      </div>
                      <div className="font-heading font-semibold text-slate-900 text-sm">{s.title}</div>
                    </div>
                    <div className="space-y-1 ms-2">
                      {(s.materials || []).map((m) => {
                        const isDone = completed.has(m.id);
                        const isActive = activeMaterial?.id === m.id;
                        return (
                          <button key={m.id} onClick={() => { setActiveMaterial(m); setActiveQuiz(null); }} className={`w-full text-start flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                            isActive ? "bg-teal-50 text-teal-800" : "text-slate-700 hover:bg-slate-50"
                          }`} data-testid={`material-btn-${m.id}`}>
                            {isDone ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : <Circle className="h-4 w-4 text-slate-300 shrink-0" />}
                            <MatIcon type={m.type} />
                            <span className="flex-1 truncate">{m.title}</span>
                          </button>
                        );
                      })}
                      {s.quiz && (
                        <button onClick={() => { setActiveQuiz(s.quiz); setActiveMaterial(null); }} className={`w-full text-start flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          activeQuiz?.id === s.quiz.id ? "bg-amber-50 text-amber-800" : "text-amber-800 hover:bg-amber-50"
                        }`} data-testid={`quiz-btn-${s.quiz.id}`}>
                          {quizScores[s.quiz.id] >= (s.quiz.passing_score || 60) ? <Trophy className="h-4 w-4 text-emerald-600 shrink-0" /> : <ClipboardCheck className="h-4 w-4 shrink-0" />}
                          <span className="flex-1 truncate font-semibold">{s.quiz.title}</span>
                          {quizScores[s.quiz.id] != null && (
                            <span className="text-xs font-bold arabic-numerals">{quizScores[s.quiz.id]}%</span>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function MatIcon({ type }) {
  const cls = "h-3.5 w-3.5 text-slate-400 shrink-0";
  if (type === "video") return <Video className={cls} />;
  if (type === "pdf") return <FileText className={cls} />;
  if (type === "link") return <Link2 className={cls} />;
  return <FileText className={cls} />;
}

function MaterialViewer({ material, completed, onComplete }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="material-viewer">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-wider text-teal-700 mb-1">{typeLabel(material.type)}</div>
          <h2 className="text-xl font-heading font-bold text-slate-900 truncate">{material.title}</h2>
        </div>
        <button onClick={onComplete} disabled={completed} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors shrink-0 ${
          completed ? "bg-emerald-50 text-emerald-700 cursor-default" : "bg-teal-700 text-white hover:bg-teal-600"
        }`} data-testid="complete-material-btn">
          <CheckCircle2 className="h-4 w-4" />
          {completed ? "تم الإكمال" : "احتساب كمكتمل"}
        </button>
      </div>
      <div className="p-4">
        {material.type === "video" && material.content_url && (
          <SignedMedia fileId={material.content_url} kind="video" className="w-full aspect-video rounded-xl bg-black" />
        )}
        {material.type === "pdf" && material.content_url && (
          <SignedMedia fileId={material.content_url} kind="iframe" title={material.title} className="w-full h-[70vh] rounded-xl border border-slate-200" />
        )}
        {material.type === "link" && material.content_url && (
          /^https?:\/\//i.test(material.content_url) ? (
            <a href={material.content_url} target="_blank" rel="noreferrer" dir="ltr" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-teal-50 text-teal-800 font-semibold hover:bg-teal-100">
              <Link2 className="h-4 w-4" /> فتح الرابط
            </a>
          ) : (
            <SignedMedia fileId={material.content_url} kind="link" title="فتح الرابط" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-teal-50 text-teal-800 font-semibold hover:bg-teal-100" />
          )
        )}
        {material.type === "text" && (
          <div className="prose prose-slate max-w-none leading-loose text-slate-800 whitespace-pre-line">{material.text_content}</div>
        )}
      </div>
    </div>
  );
}

function typeLabel(t) {
  return { video: "فيديو", pdf: "ملف PDF", text: "قراءة", link: "رابط خارجي" }[t] || t;
}

function renderVideo(url) {
  // YouTube embed
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (ytMatch) {
    return <iframe title="video" src={`https://www.youtube.com/embed/${ytMatch[1]}`} className="w-full aspect-video rounded-xl border border-slate-200" allowFullScreen />;
  }
  return <video src={url} controls className="w-full aspect-video rounded-xl bg-black" />;
}

function QuizPlayer({ quiz, bestScore, onDone, onClose }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const arr = (quiz.questions || []).map((_, i) => answers[i] ?? -1);
    setSubmitting(true);
    try {
      const { data } = await api.post(`/quizzes/${quiz.id}/submit`, { answers: arr });
      setResult(data);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (result) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden p-5 text-center" data-testid="quiz-result">
        <div className={`h-20 w-20 rounded-full mx-auto grid place-items-center ${
          result.passed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
        }`}>
          <Trophy className="h-10 w-10" />
        </div>
        <h3 className="mt-6 text-2xl font-heading font-black text-slate-900">{result.passed ? "أحسنت! نجحت 🎉" : "لم تنجح هذه المرة"}</h3>
        <div className="mt-3 text-slate-600">
          حصلت على <span className="font-bold text-slate-900 arabic-numerals">{result.correct}</span> من{" "}
          <span className="font-bold text-slate-900 arabic-numerals">{result.total}</span>
        </div>
        <div className="mt-4 text-6xl font-heading font-black text-teal-700 arabic-numerals">{result.score}%</div>
        <div className="mt-8 flex items-center justify-center gap-3">
          <button onClick={() => { setResult(null); setAnswers({}); }} className="px-6 py-3 rounded-lg bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200">
            إعادة المحاولة
          </button>
          <button onClick={onClose} className="px-6 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600">
            إغلاق
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="quiz-player">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1">اختبار</div>
          <h2 className="text-xl font-heading font-bold text-slate-900">{quiz.title}</h2>
          <div className="text-xs text-slate-500 mt-1 arabic-numerals">
            {(quiz.questions || []).length} سؤال · درجة النجاح {quiz.passing_score}%
            {bestScore != null && <span className="text-emerald-700 font-bold"> · أفضل درجاتك: {bestScore}%</span>}
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="h-5 w-5" /></button>
      </div>

      <div className="p-4 space-y-6">
        {(quiz.questions || []).map((q, qi) => (
          <div key={q.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50" data-testid={`quiz-q-${qi}`}>
            <div className="text-xs font-bold text-slate-500 mb-2 arabic-numerals">السؤال {qi + 1}</div>
            <div className="font-semibold text-slate-900 mb-4">{q.text}</div>
            <div className="space-y-2">
              {(q.options || []).map((opt, oi) => {
                const selected = answers[qi] === oi;
                return (
                  <button key={oi} onClick={() => setAnswers({ ...answers, [qi]: oi })} className={`w-full text-start flex items-center gap-3 p-3 rounded-lg border-2 text-sm transition-colors ${
                    selected ? "bg-teal-50 border-teal-500 text-teal-900" : "bg-white border-slate-200 hover:border-teal-300"
                  }`} data-testid={`quiz-q${qi}-o${oi}`}>
                    <div className={`h-5 w-5 rounded-full border-2 grid place-items-center shrink-0 ${
                      selected ? "bg-teal-700 border-teal-700 text-white" : "border-slate-300"
                    }`}>
                      {selected && <span className="text-xs">✓</span>}
                    </div>
                    <span>{opt}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
        <div className="text-sm text-slate-600 arabic-numerals">
          أجبت على {Object.keys(answers).length} من {(quiz.questions || []).length}
        </div>
        <button onClick={submit} disabled={submitting || Object.keys(answers).length === 0} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-50" data-testid="submit-quiz-btn">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          إرسال الإجابات
        </button>
      </div>
    </div>
  );
}
